import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Config, SelectedDropdownItem, TemplateState } from '../interface';
import { ItemSearchSelectFilterService } from '../item-search-select-filter.service';
import { ItemType } from '@vendasta/marketplace-apps';

@Component({
  selector: 'inventory-ui-item-search-multi-select-filter',
  templateUrl: './item-search-multi-select-filter.component.html',
  styleUrls: ['./item-search-multi-select-filter.component.scss'],
  providers: [ItemSearchSelectFilterService],
  standalone: false,
})
export class ItemSearchMultiSelectFilterComponent implements OnInit {
  @Input() config: Config = { partnerId: '' };

  @Input() set selectedItems(value: SelectedDropdownItem[]) {
    // if the app or addon is already in the list, don't add it (as it's info has already been added, eg name)
    this.itemSearchSelectFilterService.updateSelection(value);
  }

  @Output() selectionChange: EventEmitter<SelectedDropdownItem[]> = new EventEmitter<SelectedDropdownItem[]>();

  @ViewChild('chipInput') chipInput?: ElementRef<HTMLInputElement>;

  templateState$?: Observable<TemplateState>;
  searchCtrl = new UntypedFormControl('');

  constructor(private readonly itemSearchSelectFilterService: ItemSearchSelectFilterService) {}

  ngOnInit(): void {
    this.templateState$ = this.itemSearchSelectFilterService.init(
      this.searchCtrl.valueChanges.pipe(
        filter((term) => typeof term === 'string'),
        map((searchTerm) => searchTerm ?? ''),
      ),
      this.config,
      false,
    );
  }

  addItem(event: MatAutocompleteSelectedEvent, selectedItems: SelectedDropdownItem[]): void {
    const newlySelectedItem = event?.option?.value as SelectedDropdownItem;
    if (!newlySelectedItem) return;

    const isAlreadySelected = selectedItems.some(
      (i) => i.appId === newlySelectedItem.appId && i.addonId === newlySelectedItem.addonId,
    );
    if (isAlreadySelected) return;

    selectedItems = selectedItems.concat([newlySelectedItem]);
    this.resetInput();
    this.selectedItems = selectedItems;
    this.selectionChange.emit(selectedItems);
  }

  resetInput(): void {
    if (this.chipInput) {
      this.chipInput.nativeElement.value = '';
    }
    this.searchCtrl.setValue('');
  }

  removeItem(removedItem: SelectedDropdownItem, selectedItems: SelectedDropdownItem[]): void {
    selectedItems = selectedItems.filter((ra) => {
      if (this.config.filters?.itemType === ItemType.ITEM_TYPE_ADDON) {
        return ra.addonId !== removedItem.addonId;
      }
      return ra.appId !== removedItem.appId;
    });
    this.selectedItems = selectedItems;
    this.selectionChange.emit(selectedItems);
  }

  loadMore(): void {
    this.itemSearchSelectFilterService.triggerLoadMore();
  }

  displayFn = (value: unknown): string => {
    if (this.isDropdownItem(value) && value.name) {
      return value.name;
    }

    // fallback to first item's name
    return this.selectedItems?.length > 0 ? (this.selectedItems[0]?.name ?? '') : '';
  };

  isDropdownItem(data: unknown): data is SelectedDropdownItem {
    if (typeof data !== 'object' || !data) return false;
    return 'appId' in data || 'addonId' in data;
  }
}
